import { computed, ref, shallowRef, watch } from "vue";
import { defineStore } from "pinia";
import APIClient, { APIError } from "@/APIClient";
import { DraggerType, Facade } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { editingLocked } from "@/stores/EditingLockState";
import type { MergeSideColors } from "@/drawing/managers/MergeManager";
import { Logger } from "@/logger";
import { QueryKey } from "@/stores/queries/query-keys";
import { queryClient } from "@/stores/queries/query-client";
import { SaveState } from "@/stores/SaveDrawingState";
import { effectiveDrawerWidth } from "@/stores/CanvasLayoutState";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { drawingRepository } from "@/repositories/drawing.repository";
import { syncer } from "@/repositories/Syncer";
import { uploadFilesBatch } from "@/utils/upload-files.utils";
import { getDeviceFingerprint } from "@/utils/device-fingerprint";
import { reopenDrawing } from "@/utils/reopen-drawing";
import { analyzeMerge } from "@/utils/branch-merge/analyze";
import {
  buildMergePlan,
  unresolvedConflicts,
  type MergeResolutions,
} from "@/utils/branch-merge/plan";
import type {
  ConflictChoice,
  EntitySnapshot,
  MergeAnalysis,
  MergeConflict,
  MergePlan,
} from "@/utils/branch-merge/types";
import type { WorldBbox } from "@/utils/plan-analysis/types";
import { FileType } from "@/types/file.types";
import type { DRBranchWithContributors, DRDrawing } from "@/types";

/**
 * Fusion de deux versions (branches) d'un plan, depuis le plan ouvert. Store
 * partagé entre le volet de fusion et la surcouche du plan (cadre du conflit).
 *
 * 1. `analyze(cible, fusionnée)` : télécharge les DWG de la base commune et des deux
 *    têtes, les ouvre en bases annexes (le plan affiché n'est pas touché), compare.
 * 2. L'utilisateur tranche les conflits d'entités (`resolve`), un par un : le
 *    conflit courant est montré sur le plan en fantômes (`select`).
 * 3. `apply()` : applique le plan sur la base de la cible, envoie le DWG résultat
 *    comme révision de fusion, puis rouvre le plan sur le résultat.
 *
 * Préalables : en ligne, plan ouvert sans modification en attente (ni en mémoire,
 * ni en brouillon local non envoyé) — sinon la fusion partirait d'une tête qui
 * n'est pas celle que l'utilisateur voit. Revérifié avant d'appliquer : le volet
 * ne bloque pas le plan.
 */

export type MergeStep =
  | "select"
  | "analyzing"
  | "resolve"
  | "applying"
  | "done"
  | "error";

/** Clés i18n `canvas:merge.errors.*`. */
export type MergeErrorKey =
  | "unsaved"
  | "noBase"
  | "identityMismatch"
  | "stale"
  | "failed";

export interface MergeResult {
  plan: MergePlan;
  failedOps: number;
}

/** Couleurs des versions, sur le plan (fantômes) et dans le volet (pastilles). */
export const MERGE_SIDE_COLORS: Readonly<MergeSideColors> = {
  target: { r: 37, g: 132, b: 255 },
  merged: { r: 255, g: 140, b: 0 },
};

export const mergeSideCss = (side: "target" | "merged"): string => {
  const { r, g, b } = MERGE_SIDE_COLORS[side];
  return `rgb(${r}, ${g}, ${b})`;
};

/** Ouverture du volet de fusion demandée (menu fichier → CanvasDrawersMenus). */
export const mergePanelOpenRequested = ref(false);

export function requestMergePanel(): void {
  mergePanelOpenRequested.value = true;
}

function unionBbox(entities: (EntitySnapshot | null)[]): WorldBbox | null {
  const boxes = entities.map((e) => e?.bbox).filter((b): b is WorldBbox => !!b);
  if (!boxes.length) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

export const useBranchMergeState = defineStore("branchMergeState", () => {
  const step = ref<MergeStep>("select");
  const errorKey = ref<MergeErrorKey | null>(null);
  const analysis = shallowRef<MergeAnalysis | null>(null);
  const resolutions = ref<Record<string, ConflictChoice>>({});
  const result = shallowRef<MergeResult | null>(null);
  const currentConflictId = ref<string | null>(null);
  /** Versions comparées (gardées pour leurs noms après la fusion). */
  const target = shallowRef<DRBranchWithContributors | null>(null);
  const merged = shallowRef<DRBranchWithContributors | null>(null);

  /** Version en cours de lecture (1 à 3) et entités lues, pendant l'analyse. */
  const readProgress = ref<{ version: number; count: number } | null>(null);

  let drawing: DRDrawing | null = null;
  /** Change à chaque analyse ou `reset` : une analyse en cours devient caduque. */
  let analysisRun = 0;

  const conflicts = computed<MergeConflict[]>(
    () => analysis.value?.conflicts ?? [],
  );
  const remaining = computed(() =>
    analysis.value
      ? unresolvedConflicts(analysis.value, resolutions.value).length
      : 0,
  );
  const currentConflict = computed(
    () => conflicts.value.find((c) => c.id === currentConflictId.value) ?? null,
  );
  const currentIndex = computed(() =>
    conflicts.value.findIndex((c) => c.id === currentConflictId.value),
  );
  /** Cadre du conflit courant sur le plan : union des emprises de ses versions. */
  const currentBbox = computed(() => {
    const c = currentConflict.value;
    return c ? unionBbox([c.target, c.merged]) : null;
  });

  // Édition verrouillée de l'analyse jusqu'à la fin de l'application (cf.
  // EditingLockState) : aucun outil actif (navigation seule), l'outil d'avant
  // revient après.
  let draggerBeforeLock: typeof DraggerMenuState.activeDragger | null = null;
  watch(step, (s) =>
    setEditingLock(s === "analyzing" || s === "resolve" || s === "applying"),
  );

  function setEditingLock(locked: boolean) {
    if (locked === editingLocked.value) return;
    if (locked) {
      draggerBeforeLock = DraggerMenuState.activeDragger;
      DraggerMenuState.setActiveDragger({ name: DraggerType.None }, "menu");
      editingLocked.value = true;
      if (Facade.isInitialized) Facade.history.setLocked(true);
      return;
    }
    editingLocked.value = false;
    if (Facade.isInitialized) Facade.history.setLocked(false);
    if (draggerBeforeLock) {
      DraggerMenuState.setActiveDragger(draggerBeforeLock, "menu");
    }
    draggerBeforeLock = null;
  }

  function closeSession() {
    if (Facade.isInitialized) Facade.merge.closeSession();
  }

  function fail(key: MergeErrorKey, e?: unknown) {
    if (e) Logger.captureException(e, { feature: "branch_merge" });
    errorKey.value = key;
    step.value = "error";
    currentConflictId.value = null;
    closeSession();
  }

  /** Le plan ouvert n'a ni modification en mémoire ni brouillon local non envoyé. */
  async function hasPendingChanges(drawingId: number): Promise<boolean> {
    if (SaveState.value !== "saved") return true;
    const local = await localDrawingRepository.getById(drawingId);
    return !!local?.lastRevision.dwgDraft;
  }

  /** Cadre le conflit dans la partie du plan visible à gauche du volet. */
  function zoomOn(bbox: WorldBbox) {
    if (!Facade.isInitialized) return;
    // Marge serrée : l'élément doit occuper l'écran, pas s'y perdre.
    const pad = Math.max(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY, 1) * 0.6;
    const minX = bbox.minX - pad;
    const maxX = bbox.maxX + pad;
    const visible = Math.min(
      Math.max(1 - effectiveDrawerWidth.value / window.innerWidth, 0.3),
      1,
    );
    Facade.view.zoomWindow(
      minX,
      bbox.minY - pad,
      minX + (maxX - minX) / visible,
      bbox.maxY + pad,
    );
  }

  /** Rend `conflictId` courant : montré en fantômes sur le plan, cadré dessus. */
  function select(conflictId: string | null) {
    currentConflictId.value = conflictId;
    if (!Facade.isInitialized) return;
    const c = currentConflict.value;
    if (!c) return Facade.merge.clearPreview();
    Facade.merge.showConflict(c, MERGE_SIDE_COLORS);
    if (currentBbox.value) zoomOn(currentBbox.value);
  }

  function next() {
    const list = conflicts.value;
    if (!list.length) return;
    select(list[(currentIndex.value + 1) % list.length].id);
  }

  function previous() {
    const list = conflicts.value;
    if (!list.length) return;
    select(list[(currentIndex.value - 1 + list.length) % list.length].id);
  }

  async function analyze(
    d: DRDrawing,
    targetBranch: DRBranchWithContributors,
    mergedBranch: DRBranchWithContributors,
  ) {
    if (!targetBranch.headRevisionId || !mergedBranch.headRevisionId) return;
    const run = ++analysisRun;
    drawing = d;
    target.value = targetBranch;
    merged.value = mergedBranch;
    errorKey.value = null;
    resolutions.value = {};
    currentConflictId.value = null;
    readProgress.value = null;
    step.value = "analyzing";

    try {
      if (await hasPendingChanges(d.id)) {
        if (run === analysisRun) fail("unsaved");
        return;
      }

      const { baseRevisionId } = await APIClient.getMergeBase(
        d.id,
        targetBranch.headRevisionId,
        mergedBranch.headRevisionId,
      ).catch((e) => {
        throw e instanceof APIError && e.status === 404 ? "noBase" : e;
      });
      const [baseDwg, targetDwg, mergedDwg] = await Promise.all([
        APIClient.downloadRevisionDwg(d.id, baseRevisionId),
        APIClient.downloadRevisionDwg(d.id, targetBranch.headRevisionId),
        APIClient.downloadRevisionDwg(d.id, mergedBranch.headRevisionId),
      ]);

      if (run !== analysisRun) return;
      const snapshots = await Facade.merge.openSession(
        { base: baseDwg, target: targetDwg, merged: mergedDwg },
        (version, count) => (readProgress.value = { version, count }),
      );
      const a = analyzeMerge(
        snapshots.base,
        snapshots.target,
        snapshots.merged,
      );
      if (a.identityMismatch) {
        Logger.captureException(
          new Error("BranchMergeState : appariement des entités non fiable"),
          {
            drawingId: d.id,
            matchRatio: a.matchRatio,
            reappearedRatio: a.reappearedRatio,
          },
        );
        return fail("identityMismatch");
      }
      Facade.merge.describeConflicts(a.conflicts);
      analysis.value = a;
      step.value = "resolve";
      select(a.conflicts[0]?.id ?? null);
    } catch (e) {
      // Volet fermé ou nouvelle analyse entre-temps : plus personne n'attend.
      if (run !== analysisRun) return;
      if (e === "noBase") return fail("noBase");
      fail("failed", e);
    }
  }

  function resolve(conflictId: string, choice: ConflictChoice) {
    resolutions.value = { ...resolutions.value, [conflictId]: choice };
  }

  /** Tranche d'un coup tous les conflits encore ouverts. */
  function resolveAll(choice: "target" | "merged") {
    const resolved = { ...resolutions.value };
    for (const c of conflicts.value) {
      if (!resolved[c.id]) resolved[c.id] = choice;
    }
    resolutions.value = resolved;
  }

  async function apply() {
    const d = drawing;
    const a = analysis.value;
    const t = target.value;
    const m = merged.value;
    if (!d || !a || !t || !m) return;
    if (!Facade.isInitialized || !Facade.merge.hasSession) return;
    if (remaining.value > 0) return;
    step.value = "applying";
    currentConflictId.value = null;
    Facade.merge.clearPreview();

    try {
      // Le volet laisse le plan éditable : une modification faite pendant la
      // résolution serait écrasée par le résultat.
      if (await hasPendingChanges(d.id)) return fail("unsaved");

      const observations = d.diagnostic
        ? await APIClient.getFieldObservations(d.diagnostic.id)
        : null;
      const validObservationIds = observations
        ? new Set(
            observations
              .map((o) => o.localId)
              .filter((id): id is string => !!id),
          )
        : undefined;

      const plan = buildMergePlan(a, resolutions.value as MergeResolutions, {
        validObservationIds,
      });

      // Ferme la session : la cible est modifiée.
      const { report, dwg } = Facade.merge.apply(plan);

      const [upload] = await uploadFilesBatch([
        {
          file: new File([dwg], `${d.name}.dwg`, {
            type: "application/octet-stream",
          }),
          fileType: FileType.REVISION_DWG,
          params: { drawingId: d.id },
        },
      ]);
      if (!upload?.fileId || !upload.success) {
        throw new Error("BranchMergeState : échec de l'envoi du DWG fusionné");
      }

      const updated = await APIClient.mergeRevisions(d.id, {
        fileId: upload.fileId,
        targetRevisionId: t.headRevisionId as number,
        mergedRevisionId: m.headRevisionId as number,
        fromDevice: getDeviceFingerprint(),
        uidAliases: plan.uidAliases,
      });

      // Le plan ouvert passe sur le résultat (même chemin que la restauration
      // d'une sauvegarde) ; l'oplog et les images suivent la nouvelle tête.
      const buffer = dwg.buffer.slice(
        dwg.byteOffset,
        dwg.byteOffset + dwg.byteLength,
      ) as ArrayBuffer;
      await drawingRepository.replace(updated);
      await localDrawingRepository.saveLastRevisionDwg(updated, buffer);
      await syncer.makeOplogLocal(d.id);
      await syncer.syncDrawingImages(d.id, updated.lastRevision.rasterPaths);
      await reopenDrawing(d.id, buffer);
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DRAWING_BRANCHES, d.id],
      });

      // Chaque opération non appliquée est déjà remontée à Sentry (applyMergePlan).
      result.value = { plan, failedOps: report.failed.length };
      step.value = "done";
    } catch (e) {
      if (e instanceof APIError && e.status === 409) return fail("stale");
      fail("failed", e);
    }
  }

  function reset() {
    analysisRun++;
    closeSession();
    step.value = "select";
    errorKey.value = null;
    analysis.value = null;
    resolutions.value = {};
    result.value = null;
    currentConflictId.value = null;
    readProgress.value = null;
    drawing = null;
  }

  return {
    step,
    errorKey,
    analysis,
    readProgress,
    resolutions,
    remaining,
    result,
    target,
    merged,
    conflicts,
    currentConflictId,
    currentConflict,
    currentIndex,
    currentBbox,
    analyze,
    resolve,
    resolveAll,
    select,
    next,
    previous,
    apply,
    reset,
  };
});
